import { supabase } from '../lib/supabase';

export const staffService = {
  /**
   * Get all staff by role (including inactive) - for admin management
   */
  async getAllStaffByRole(role) {
    console.log('=== getAllStaffByRole called with role:', role);

    // Get all staff records (active and inactive)
    const { data: staffData, error: staffError } = await supabase
      ?.from('staff')
      ?.select('*')
      ?.eq('role', role);

    if (staffError) {
      console.error('Error fetching all staff by role:', staffError);
      console.error('Error details:', JSON.stringify(staffError, null, 2));
      throw staffError;
    }

    return this._transformStaffData(staffData);
  },

  /**
   * Get all active staff by role - for booking/scheduling
   */
  async getStaffByRole(role) {
    console.log('=== getStaffByRole called with role:', role);

    // First, get active staff records only
    const { data: staffData, error: staffError } = await supabase
      ?.from('staff')
      ?.select('*')
      ?.eq('role', role)
      ?.eq('is_active', true)
      ?.is('date_of_exit', null);

    if (staffError) {
      console.error('Error fetching staff by role:', staffError);
      console.error('Error details:', JSON.stringify(staffError, null, 2));
      throw staffError;
    }

    return this._transformStaffData(staffData);
  },

  /**
   * Update staff active status
   */
  async updateStaffStatus(staffId, isActive) {
    console.log('=== updateStaffStatus called:', { staffId, isActive });

    const { data, error } = await supabase
      ?.from('staff')
      ?.update({ is_active: isActive })
      ?.eq('id', staffId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error updating staff status:', error);
      throw error;
    }

    console.log('✅ Staff status updated successfully:', data);
    return data;
  },

  /**
   * Internal method to transform staff data with user profiles
   */
  async _transformStaffData(staffData) {
    console.log('=== Raw staff data from DB:', JSON.stringify(staffData, null, 2));

    // Get all user IDs
    const userIds = staffData?.map(s => s.user_id).filter(Boolean);
    console.log('=== User IDs to fetch:', userIds);

    // Fetch user profiles separately
    const { data: profilesData, error: profilesError } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.in('id', userIds);

    if (profilesError) {
      console.error('Error fetching user profiles:', profilesError);
    }

    console.log('=== User profiles data:', JSON.stringify(profilesData, null, 2));

    // Create a map of user profiles by ID
    const profilesMap = {};
    profilesData?.forEach(profile => {
      profilesMap[profile.id] = profile;
    });

    const transformedData = staffData?.map(staff => {
      const userProfile = profilesMap[staff.user_id];

      console.log('Processing staff record:', {
        id: staff?.id,
        user_id: staff?.user_id,
        userProfile: userProfile
      });

      return {
        id: staff?.id,
        userId: staff?.user_id,
        fullName: userProfile?.full_name || 'Unknown',
        email: userProfile?.email || '',
        phone: userProfile?.phone || '',
        avatarUrl: userProfile?.avatar_url || staff?.profile_image_url,
        role: staff?.role,
        gender: staff?.gender || userProfile?.gender,
        specializations: staff?.specializations || [],
        qualifications: staff?.qualifications || [],
        experienceYears: staff?.experience_years,
        rating: staff?.rating,
        consultationFee: staff?.consultation_fee, // Note: staff table still uses consultation_fee
        bio: staff?.bio,
        profileImageUrl: staff?.profile_image_url,
        isActive: staff?.is_active,
        dateOfBirth: userProfile?.date_of_birth
      };
    });

    console.log('=== Transformed staff data:', transformedData);
    return transformedData;
  },

  /**
   * Get available therapists for therapy bookings (excludes doctor consultation appointments)
   * UPDATED: Now supports dynamic duration and therapists_required parameters
   *
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {string} time - Time in HH:MM format
   * @param {string|null} gender - Optional gender filter ('male' or 'female')
   * @param {number} durationMinutes - Service duration in minutes (default: 60)
   * @param {number} therapistsRequired - Number of therapists required (default: 1)
   * @returns {Promise<Array>} Array of available therapists
   */
  async getAvailableTherapistsForTherapy(date, time, gender = null, durationMinutes = 60, therapistsRequired = 1) {
    console.log('🔍 getAvailableTherapistsForTherapy called with:', {
      date,
      time,
      gender,
      durationMinutes,
      therapistsRequired
    });

    const { data, error } = await supabase?.rpc('get_available_therapists_for_therapy', {
      p_date: date,
      p_time: time,
      p_gender: gender,
      p_duration_minutes: durationMinutes,
      p_therapists_required: therapistsRequired
    });

    if (error) {
      console.error('❌ Error getting available therapists for therapy:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      throw error;
    }

    console.log('✅ RPC returned data:', data);
    console.log(`Number of therapists found: ${data?.length || 0} (required: ${therapistsRequired})`);

    return data?.map(staff => ({
      id: staff?.staff_id,
      staffId: staff?.staff_id,
      fullName: staff?.full_name,
      gender: staff?.gender,
      specializations: staff?.specializations || [],
      rating: staff?.rating
    })) || [];
  },

  /**
   * REMOVED: getAvailableStaff() - Obsolete method removed in Phase 0 cleanup
   *
   * This method used the obsolete RPC function 'get_available_staff_for_slot' which has been dropped.
   *
   * REPLACEMENT:
   * - For therapy bookings: Use getAvailableTherapistsForTherapy()
   * - For doctor consultations: Use getAvailableDoctorsForConsultation()
   *
   * See: DATABASE_FUNCTION_AUDIT_AND_CLEANUP.md
   * Migration: 20251216000002_cleanup_obsolete_functions.sql
   */

  /**
   * REMOVED: checkStaffAvailability() - Obsolete method removed in Phase 0 cleanup
   *
   * This method used the obsolete RPC function 'check_staff_availability_for_slot' which has been dropped.
   *
   * REPLACEMENT:
   * - For therapy bookings: The availability is checked within getAvailableTherapistsForTherapy()
   * - For doctor consultations: The availability is checked within getAvailableDoctorsForConsultation()
   *
   * See: DATABASE_FUNCTION_AUDIT_AND_CLEANUP.md
   * Migration: 20251216000002_cleanup_obsolete_functions.sql
   */

  /**
   * Get staff availability schedule
   */
  async getStaffAvailability(staffId) {
    const { data, error } = await supabase?.from('staff_availability')?.select('*')?.eq('staff_id', staffId)?.eq('is_available', true)?.order('day_of_week');

    if (error) throw error;

    return data?.map(schedule => ({
      id: schedule?.id,
      staffId: schedule?.staff_id,
      dayOfWeek: schedule?.day_of_week,
      startTime: schedule?.start_time,
      endTime: schedule?.end_time,
      breakStartTime: schedule?.break_start_time,
      breakEndTime: schedule?.break_end_time,
      slotDurationMinutes: schedule?.slot_duration_minutes,
      maxBookingsPerSlot: schedule?.max_bookings_per_slot,
      notes: schedule?.notes
    }));
  },

  /**
   * Get available time slots for doctor consultations (excludes therapy appointments)
   */
  async getAvailableSlotsForConsultation(staffId, date) {
    const dayOfWeek = new Date(date)?.getDay();

    // Get staff schedule for the day
    const { data: schedules, error: scheduleError } = await supabase
      ?.from('staff_availability')
      ?.select('*')
      ?.eq('staff_id', staffId)
      ?.eq('day_of_week', dayOfWeek)
      ?.eq('is_available', true);

    if (scheduleError) throw scheduleError;
    if (!schedules || schedules?.length === 0) return [];

    const schedule = schedules?.[0];

    // Get existing CONSULTATION bookings only for the date
    const { data: bookings, error: bookingError } = await supabase
      ?.from('appointments')
      ?.select('preferred_time, status, booking_type')
      ?.eq('doctor_id', staffId)
      ?.eq('preferred_date', date)
      ?.eq('booking_type', 'consultation')
      ?.not('status', 'in', '(cancelled,no_show)');

    if (bookingError) throw bookingError;

    return this._generateTimeSlots(schedule, bookings, date);
  },

  /**
   * Get available time slots for a staff member on a specific date (legacy method)
   */
  async getAvailableSlots(staffId, date) {
    const dayOfWeek = new Date(date)?.getDay();

    // Get staff schedule for the day
    const { data: schedules, error: scheduleError } = await supabase
      ?.from('staff_availability')
      ?.select('*')
      ?.eq('staff_id', staffId)
      ?.eq('day_of_week', dayOfWeek)
      ?.eq('is_available', true);

    if (scheduleError) throw scheduleError;
    if (!schedules || schedules?.length === 0) return [];

    const schedule = schedules?.[0];

    // Get existing bookings for the date
    const { data: bookings, error: bookingError } = await supabase
      ?.from('appointments')
      ?.select('preferred_time, status')
      ?.or(`therapist_id.eq.${staffId},doctor_id.eq.${staffId}`)
      ?.eq('preferred_date', date)
      ?.not('status', 'in', '(cancelled,no_show)');

    if (bookingError) throw bookingError;

    return this._generateTimeSlots(schedule, bookings, date);
  },

  /**
   * Internal method to generate time slots based on schedule and bookings
   */
  _generateTimeSlots(schedule, bookings, date) {

    // Generate time slots
    const slots = [];
    const startTime = schedule?.start_time;
    const endTime = schedule?.end_time;
    const breakStart = schedule?.break_start_time;
    const breakEnd = schedule?.break_end_time;
    const slotDuration = schedule?.slot_duration_minutes || 30;
    const maxBookings = schedule?.max_bookings_per_slot || 1;

    let currentTime = new Date(`2000-01-01T${startTime}`);
    const endDateTime = new Date(`2000-01-01T${endTime}`);

    // Get current time for filtering past slots
    const now = new Date();

    // Parse the selected date (format: YYYY-MM-DD)
    const [year, month, day] = date.split('-').map(Number);
    const selectedDate = new Date(year, month - 1, day); // month is 0-indexed
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const isToday = selectedDate.getTime() === today.getTime();

    console.log('=== Time Slot Generation ===');
    console.log('Current time:', now.toISOString());
    console.log('Selected date string:', date);
    console.log('Selected date object:', selectedDate.toISOString());
    console.log('Today date object:', today.toISOString());
    console.log('Is today:', isToday);

    while (currentTime < endDateTime) {
      const hours = currentTime.getHours();
      const minutes = currentTime.getMinutes();
      const timeString = currentTime.toTimeString().slice(0, 5);

      // Skip if in break time (don't show break slots at all)
      if (breakStart && breakEnd) {
        const breakStartTime = new Date(`2000-01-01T${breakStart}`);
        const breakEndTime = new Date(`2000-01-01T${breakEnd}`);
        if (currentTime >= breakStartTime && currentTime < breakEndTime) {
          currentTime?.setMinutes(currentTime?.getMinutes() + slotDuration);
          continue;
        }
      }

      // Check if this slot is in the past (for today only)
      let isPast = false;
      if (isToday) {
        // Create a datetime for this slot on the selected date
        const slotDateTime = new Date(year, month - 1, day, hours, minutes);
        isPast = slotDateTime <= now;

        console.log(`Checking slot ${timeString}:`, {
          slotDateTime: slotDateTime.toISOString(),
          now: now.toISOString(),
          isPast,
          slotTime: slotDateTime.getTime(),
          nowTime: now.getTime()
        });
      }

      // Count existing bookings for this slot (now checks both therapist_id and doctor_id)
      const bookingCount = bookings?.filter(b => b?.preferred_time === timeString)?.length || 0;
      const isFullyBooked = bookingCount >= maxBookings;

      // Include ALL slots (past, booked, available) - let frontend handle display
      slots?.push({
        time: timeString,
        available: !isPast && !isFullyBooked, // Available only if not past AND not fully booked
        bookingCount,
        maxBookings,
        isPast, // Add isPast flag for frontend to use
        isBooked: isFullyBooked // Add isBooked flag for frontend to use
      });

      currentTime?.setMinutes(currentTime?.getMinutes() + slotDuration);
    }

    return slots;
  }
};